import type { Character } from '../domain/character.ts';
import type { StatId } from '../domain/stats.ts';
import { STAT_LABELS } from '../domain/stats.ts';
import type { Skill } from '../content/types.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import type { CheckComponent, CheckResult } from '../rules/checks.ts';
import { resolveSkillCheck, resolveStatCheck } from '../rules/checks.ts';
import type { EffectiveCharacter } from './effectiveCharacter.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';
import type { CheckContext } from '../rules/effects.ts';
import { canRollSkill } from '../rules/skillAvailability.ts';
import { resolveEvadeCheck } from '../rules/evade.ts';
import type { Resolution } from '../rules/degrees.ts';
import type { CheckHistoryEntry } from '../rules/rollHistory.ts';

/**
 * Composition layer: turns a Character plus the player's Stat and Skill
 * selection into rules-engine inputs. Contains no rules of its own.
 *
 * RULE DCC-CHK-006 - the Stat used is the one the PLAYER selected. A Skill's
 * printed Stat is only its Common Stat and is never applied automatically.
 */

/**
 * Kept for callers that hold only a plain Character and no Effects. Both
 * delegate to the same effective-character machinery, so there is exactly one
 * Enhanced Stat implementation.
 */
export function enhancedStatsFor(character: Character) {
  return buildEffectiveCharacter(character).enhancedStats;
}

export function statModFor(character: Character, stat: StatId): number {
  return buildEffectiveCharacter(character).statMod(stat);
}

/** The Check being made, for matching scoped Advantage sources. */
export function checkContextFor(selection: RollSelection, kind: CheckContext['kind']): CheckContext {
  return {
    kind,
    stat: selection.stat,
    skillId: selection.skill ? selection.skill.id : null,
  };
}

export const UNSKILLED_ID = '__unskilled__';
export const UNSKILLED_CHECK: Skill = {
  id: UNSKILLED_ID, name: 'Unskilled', kind: 'utility', traits: [], restriction: null,
  commonStat: null, manaCost: null, advancement: 'never-through-use', advancementNote: null,
  source: { book: 'custom', createdAt: '2026-10-14T00:00:00.000Z' },
};

/** What the player has selected in the two columns of the Play Sheet. */
export interface RollSelection {
  stat: StatId | null;
  skill: Skill | null;
}

export type RollKind = 'skill-check' | 'stat-check' | 'evade' | 'none';

export interface RollPlan {
  kind: RollKind;
  /** True when Roll should be enabled. */
  ready: boolean;
  /** Heading, e.g. "INT + Negotiation". */
  label: string;
  /** The exact formula, spelled out. */
  formula: string;
  components: CheckComponent[];
  totalModifier: number;
  /** Why the roll cannot be made, when ready is false. */
  blockedReason: string | null;
  /** Set when the player has picked a Skill but no Stat yet. */
  needsStat: boolean;
  /** The Skill's Common Stat, offered as a one-click convenience. */
  commonStat: StatId | null;
  untrained: boolean;
}

/**
 * Works out what would happen, without rolling. The Roll Tray renders this, so
 * the displayed formula and the executed roll can never disagree.
 */
export function planRollFor(effective: EffectiveCharacter, selection: RollSelection): RollPlan {
  const { stat, skill } = selection;
  const empty: RollPlan = {
    kind: 'none',
    ready: false,
    label: 'Nothing selected',
    formula: 'Select a Stat, a Skill, or both.',
    components: [],
    totalModifier: 0,
    blockedReason: null,
    needsStat: false,
    commonStat: null,
    untrained: false,
  };

  if (!stat && !skill) return empty;

  // Skill selected, no Stat: prompt rather than silently using the Common Stat.
  if (!stat && skill) {
    return {
      ...empty,
      kind: 'skill-check',
      label: skill.name,
      formula: 'Select a Stat to complete this Check.',
      blockedReason: null,
      needsStat: true,
      commonStat: skill.commonStat,
    };
  }

  if (stat && !skill) {
    const mod = effective.statMod(stat);
    const extras = effective.checkComponentsFor(checkContextFor(selection, 'stat'));
    const components = [{ label: `${STAT_LABELS[stat].abbr} Mod`, value: mod }, ...extras];
    return {
      kind: 'stat-check',
      ready: true,
      label: `${STAT_LABELS[stat].full} Check`,
      formula: `d20 + ${STAT_LABELS[stat].abbr} Mod (${signed(mod)})${extras
        .map((e) => ` ${signed(e.value)} (${e.label})`)
        .join('')}`,
      components,
      totalModifier: components.reduce((acc, c) => acc + c.value, 0),
      blockedReason: null,
      needsStat: false,
      commonStat: null,
      untrained: false,
    };
  }

  // Both selected.
  const chosenStat = stat as StatId;
  const chosenSkill = skill as Skill;
  // Effective Rank is the Rank available for this Check. Equipment may grant a
  // Skill temporarily from a stored Rank of zero; the sheet and the roll must
  // agree that such a Skill is presently trained while the Item is equipped.
  const rank = chosenSkill.id === UNSKILLED_ID ? 0 : effective.effectiveSkillRank(chosenSkill.id);
  const availability = canRollSkill(chosenSkill, rank);
  const mod = effective.statMod(chosenStat);
  const abbr = STAT_LABELS[chosenStat].abbr;

  if (!availability.rollable) {
    return {
      ...empty,
      kind: 'skill-check',
      label: `${abbr.toUpperCase()} + ${chosenSkill.name}`,
      formula: '',
      blockedReason: availability.reason,
      commonStat: chosenSkill.commonStat,
    };
  }

  const untrained = availability.untrained;
  const components: CheckComponent[] = [];
  if (!untrained) components.push({ label: `${chosenSkill.name} Rank`, value: rank });
  components.push({ label: `${abbr} Mod`, value: mod });
  for (const extra of effective.checkComponentsFor(checkContextFor(selection, 'skill'))) {
    components.push(extra);
  }
  const totalModifier = components.reduce((acc, c) => acc + c.value, 0);

  return {
    kind: 'skill-check',
    ready: true,
    label: `${abbr.toUpperCase()} + ${chosenSkill.name}`,
    formula: untrained
      ? `d20 (Disadvantage, untrained) + ${abbr} Mod (${signed(mod)})`
      : `d20 + ${abbr} Mod (${signed(mod)}) + ${chosenSkill.name} Rank (${signed(rank)})`,
    components,
    totalModifier,
    blockedReason: null,
    needsStat: false,
    commonStat: chosenSkill.commonStat,
    untrained,
  };
}

export type RollOutcome =
  | { ok: true; result: CheckResult }
  | { ok: false; reason: string };

/** Executes the plan. The Stat used is always the selected one. */
export function executeRollFor(
  roller: Roller,
  effective: EffectiveCharacter,
  selection: RollSelection,
  mode: RollMode,
): RollOutcome {
  const plan = planRollFor(effective, selection);
  if (!plan.ready) {
    return { ok: false, reason: plan.blockedReason ?? 'Select a Stat or a Skill first.' };
  }

  const stat = selection.stat as StatId;

  if (plan.kind === 'stat-check') {
    return {
      ok: true,
      result: resolveStatCheck(roller, {
        stat,
        statLabel: STAT_LABELS[stat].full,
        statAbbr: STAT_LABELS[stat].abbr,
        statMod: effective.statMod(stat),
        extraComponents: effective.checkComponentsFor(checkContextFor(selection, 'stat')),
        // The player's choice is one source among the Effect sources.
        mode: effective.rollMode(mode, checkContextFor(selection, 'stat')),
      }),
    };
  }

  const skill = selection.skill as Skill;
  return {
    ok: true,
    result: resolveSkillCheck(roller, {
      skillId: skill.id,
      skillName: skill.name,
      stat,
      statAbbr: STAT_LABELS[stat].abbr,
      rank: skill.id === UNSKILLED_ID ? 0 : effective.effectiveSkillRank(skill.id),
      statMod: effective.statMod(stat),
      extraComponents: effective.checkComponentsFor(checkContextFor(selection, 'skill')),
      mode: effective.rollMode(mode, checkContextFor(selection, 'skill')),
    }),
  };
}

/** Convenience wrappers for callers holding a plain Character. */
/**
 * Catalogue-free convenience: no Buffs, no Debuffs, no worn gear.
 *
 * For rules tests asking what a Skill does in isolation. Everything the player
 * sees or rolls goes through `performRoll`, which builds the crawler from its
 * runtime Effects AND its equipment.
 */
export function planRoll(character: Character, selection: RollSelection): RollPlan {
  return planRollFor(buildEffectiveCharacter(character), selection);
}

export function executeRoll(
  roller: Roller,
  character: Character,
  selection: RollSelection,
  mode: RollMode,
): RollOutcome {
  return executeRollFor(roller, buildEffectiveCharacter(character), selection, mode);
}

export function rollEvadeFor(
  roller: Roller,
  effective: EffectiveCharacter,
  mode: RollMode,
): CheckResult {
  // Evade adds only the Dex Mod (C&C p.14), plus any explicit Evade modifier.
  const modifier = effective.statMod('dex') + effective.modifiers.evade;
  const context: CheckContext = { kind: 'evade', stat: 'dex', skillId: null };
  return resolveEvadeCheck(roller, modifier, effective.rollMode(mode, context));
}

export function rollEvade(roller: Roller, character: Character, mode: RollMode): CheckResult {
  return rollEvadeFor(roller, buildEffectiveCharacter(character), mode);
}

/**
 * Freezes a completed roll into a history entry.
 * Every modifier is copied as a NUMBER, never as a reference to the character,
 * so later Stat or Rank changes cannot rewrite history.
 */
export function toHistoryEntry(args: {
  id: string;
  timestamp: string;
  result: CheckResult;
  selection: RollSelection;
  character: Character;
  resolution: Resolution | null;
  /** The resolved view the roll was made with, for Effect provenance. */
  effective?: EffectiveCharacter;
  /** What the player asked for, before Effect sources were folded in. */
  manualMode?: RollMode;
}): CheckHistoryEntry {
  const { result, selection, character } = args;
  const kind = result.kind as CheckHistoryEntry['rollType'];
  const stat = selection.stat;
  const skill = result.kind === 'skill' ? selection.skill : null;

  // Read the Mod from the EFFECTIVE view the roll was actually made with.
  // Falling back to the raw character here would record a Buffed roll with its
  // unbuffed Mod, and the stored total would not add up.
  const view = args.effective ?? buildEffectiveCharacter(character);
  const statMod =
    result.kind === 'evade' ? view.statMod('dex') : stat ? view.statMod(stat) : null;

  const rankComponent = result.components.find((c) => c.label.endsWith('Rank'));
  const context = checkContextFor(selection, kind);
  const effectSources = args.effective ? args.effective.effectSourcesFor(context) : [];

  return {
    id: args.id,
    timestamp: args.timestamp,
    rollType: kind,
    label:
      result.kind === 'evade'
        ? 'Evade'
        : skill && stat
          ? `${STAT_LABELS[stat].abbr.toUpperCase()} + ${skill.name}`
          : result.label,
    statId: result.kind === 'evade' ? 'dex' : stat,
    statAbbr: result.kind === 'evade' ? 'Dex' : stat ? STAT_LABELS[stat].abbr : null,
    statMod,
    skillId: skill && skill.id !== UNSKILLED_ID ? skill.id : null,
    skillName: skill ? skill.name : null,
    skillRank: rankComponent ? rankComponent.value : skill ? 0 : null,
    mode: result.die.mode,
    manualMode: args.manualMode ?? result.die.mode,
    effectSources,
    dice: [...result.die.dice],
    keptDie: result.die.kept,
    extraComponents: result.components
      .filter((c) => !c.label.endsWith('Rank') && !c.label.endsWith('Mod'))
      .map((c) => ({ ...c })),
    total: result.total,
    untrained: result.untrained,
    difficulty: args.resolution ? args.resolution.difficulty : null,
    degree: args.resolution ? args.resolution.degree : null,
  };
}

function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value);
}
